import React from 'react';
import PropTypes from 'prop-types';
import { useFormikContext, getIn } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';

import Field from 'core/components/form/form.v2';
import Section from 'core/components/section';
import { Label } from 'core/components/label';
import { SubRiskCollapsibleSection } from 'common/components/farm/locations/risks/sub-risks/sub-risk-collapsible-section';
import { ActionButton } from 'core/components/action-button';
import { AppliedTag } from 'common/components/farm/applied-tag';
import deleteIcon from 'core/assets/svg/delete-icon.svg';
import addIcon from 'core/assets/svg/ic-add.svg';
import withDatePicker from 'core/components/with-date-picker';
import useStyles from './personal-umbrella-liability.styles';
import { EditControl } from 'common/disabled-context/read-only';

// CFM Personal Umbrella Liability endorsement — full question flow (#33893). Data-capture only;
// Clarion Door rates the premium (config pending). Fixed terms: $1,000,000 limit / $500 SIR.
const BASE = 'farmCoverage.personalUmbrella';

const cslTierOptions = [
  { id: '300000', value: '$300,000 CSL' },
  { id: '500000+', value: '$500,000+ CSL' }
];
const primaryAutoOptions = [
  { id: 'Vehicle', value: 'Vehicle' },
  { id: 'NoVehicle', value: 'No Vehicle' },
  { id: 'NamedNonOwned', value: 'Named Non-Owned' }
];
const primaryAutoLimitOptions = [
  { id: 'NoVehicle', value: 'No Vehicle' },
  { id: '250/500/100 or 500 CSL', value: '250/500/100 or 500 CSL' },
  { id: '500/500/100+', value: '500/500/100+' }
];
const additionalAutoLimitOptions = [
  { id: '250/500/100', value: '250/500/100' },
  { id: '500/500/100+', value: '500/500/100+' }
];
const watercraftCategoryOptions = [
  { id: 'Outboard', value: 'Outboard' },
  { id: 'Inboard', value: 'Inboard' },
  { id: 'Sailboat', value: 'Sailboat' },
  { id: 'PersonalWatercraft', value: 'Personal Watercraft (Jet Ski)' }
];
const horsepowerBandOptions = [
  { id: '29-49', value: '29–49 HP' },
  { id: '50-99', value: '50–99 HP' },
  { id: '100+', value: '100+ HP' }
];
const lengthBandOptions = [
  { id: 'Under26', value: 'Under 26 Ft' },
  { id: '26-40', value: '26–40 Ft' }
];
const speedBandOptions = [
  { id: 'Under16', value: 'Under 16 MPH' },
  { id: '16-30', value: '16–30 MPH' },
  { id: 'Over30', value: 'Over 30 MPH' }
];
const recVehicleCategoryOptions = [
  { id: 'ATV', value: 'ATV' },
  { id: 'Snowmobile', value: 'Snowmobile' }
];

// Reusable list editor for the umbrella's repeating sub-objects (drivers, residences, watercraft,
// etc). Mirrors the domestic-employees pattern: collapsible section + add/remove rows.
const UmbrellaList = ({ title, fieldName, newItem, addLabel, disabled, onChangeActive, renderRow }) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const items = getIn(values, fieldName) || [];

  const handleAdd = () => {
    setFieldValue(fieldName, [...items, { id: uuidv4(), ...newItem }]);
    onChangeActive();
  };
  const handleRemove = (index) => {
    setFieldValue(
      fieldName,
      items.filter((_, i) => i !== index)
    );
    onChangeActive();
  };

  return (
    <SubRiskCollapsibleSection
      type="secondary"
      content={<div className={classes.headerText}>{title}</div>}
      contentRight={items.length > 0 ? <AppliedTag /> : null}
      initialOpen={items.length > 0}
    >
      {items.map((item, index) => (
        <Grid container key={item.id} alignItems="center">
          <Grid item xs={11}>
            <Grid container spacing={2}>
              {renderRow(index)}
            </Grid>
          </Grid>
          <Grid item xs={1}>
            <EditControl>
              <ActionButton onClick={() => handleRemove(index)} icon={deleteIcon} disabled={disabled} size="small" />
            </EditControl>
          </Grid>
        </Grid>
      ))}
      <Grid container>
        <EditControl>
          <Button
            className={classes.addAnotherButton}
            disabled={disabled}
            onClick={handleAdd}
            startIcon={<img src={addIcon} alt="add" width={28} height={28} />}
          >
            {addLabel}
          </Button>
        </EditControl>
      </Grid>
    </SubRiskCollapsibleSection>
  );
};

UmbrellaList.propTypes = {
  title: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  newItem: PropTypes.object.isRequired,
  addLabel: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  onChangeActive: PropTypes.func.isRequired,
  renderRow: PropTypes.func.isRequired
};

// Compact address row (address / city / state / zip) used by the residence lists and the named
// premises exclusion.
const addressRow = (prefix, disabled) => (
  <>
    <Field
      xs={6}
      type="string"
      mode="dark"
      label="Address"
      id={`${prefix}.address`}
      name={`${prefix}.address`}
      disabled={disabled}
    />
    <Field
      xs={3}
      type="string"
      mode="dark"
      label="City"
      id={`${prefix}.city`}
      name={`${prefix}.city`}
      disabled={disabled}
    />
    <Field
      xs={2}
      type="state"
      mode="dark"
      label="State"
      id={`${prefix}.state`}
      name={`${prefix}.state`}
      disabled={disabled}
    />
    <Field
      xs={1}
      type="string"
      mode="dark"
      label="Zip"
      id={`${prefix}.zip`}
      name={`${prefix}.zip`}
      disabled={disabled}
    />
  </>
);

const PersonalUmbrellaLiability = ({ disabled = false, onChangeActive }) => {
  const classes = useStyles();
  const { values } = useFormikContext();
  const umbrella = values.farmCoverage?.personalUmbrella || {};
  // year / make / model row, shared by the additional-automobile and antique/classic vehicle lists
  const vehicleRow = (listName) => (index) => (
    <>
      <Field
        xs={4}
        type="numeric"
        mode="dark"
        label="Year"
        id={`${BASE}.${listName}[${index}].year`}
        name={`${BASE}.${listName}[${index}].year`}
        disabled={disabled}
      />
      <Field
        xs={4}
        type="string"
        mode="dark"
        label="Make"
        id={`${BASE}.${listName}[${index}].make`}
        name={`${BASE}.${listName}[${index}].make`}
        disabled={disabled}
      />
      <Field
        xs={4}
        type="string"
        mode="dark"
        label="Model"
        id={`${BASE}.${listName}[${index}].model`}
        name={`${BASE}.${listName}[${index}].model`}
        disabled={disabled}
      />
    </>
  );
  const residenceRow = (listName) => (index) => (
    <>
      {addressRow(`${BASE}.${listName}[${index}].addressDetails`, disabled)}
      <Field
        xs={6}
        type="select"
        mode="dark"
        label="Residence limit"
        options={cslTierOptions}
        id={`${BASE}.${listName}[${index}].combinedSingleLimit`}
        name={`${BASE}.${listName}[${index}].combinedSingleLimit`}
        disabled={disabled}
      />
    </>
  );

  return (
    <Section title="Personal Umbrella Liability" type="SubSection" id="farmPersonalUmbrella">
      {/* #35219 — dark card to match the endorsements/liability sections; every field below is mode="dark" */}
      <Grid container spacing={2} alignItems="flex-start" className={classes.umbrellaContainer}>
        <Label type="infoLabelWhite">
          $1,000,000 Per Occurrence / $1,000,000 Annual Aggregate limit with a $500 Self-Insured Retention. Requires the
          insured to carry both Home and Auto coverage with CFM.
        </Label>

        {/* 3.0 Eligibility gate — hard-stop when the insured does not carry CFM Auto */}
        <Field
          xs={6}
          type="checkbox"
          mode="dark"
          label="Insured carries CFM Auto insurance"
          id={`${BASE}.hasCfmAuto`}
          name={`${BASE}.hasCfmAuto`}
          disabled={disabled}
          onChange={onChangeActive}
        />
        {!umbrella.hasCfmAuto ? (
          <Label type="infoLabelWhite">
            Personal Umbrella Liability requires an in-force CFM Auto policy. Confirm CFM Auto to continue; without it
            the endorsement cannot be quoted or bound.
          </Label>
        ) : (
          <>
            {/* 3.1 Initial residence CSL tier */}
            <Field
              xs={6}
              type="select"
              mode="dark"
              label="Combined Single Limit (initial residence)"
              options={cslTierOptions}
              id={`${BASE}.combinedSingleLimit`}
              name={`${BASE}.combinedSingleLimit`}
              disabled={disabled}
              onChange={onChangeActive}
            />

            {/* 3.3 Primary automobile */}
            <Label type="formSubTitle">Automobiles</Label>
            <Grid container spacing={2}>
              <Field
                xs={4}
                type="select"
                mode="dark"
                label="Primary automobile"
                options={primaryAutoOptions}
                id={`${BASE}.primaryAutomobile`}
                name={`${BASE}.primaryAutomobile`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              <Field
                xs={4}
                type="select"
                mode="dark"
                label="Underlying auto liability limit"
                options={primaryAutoLimitOptions}
                id={`${BASE}.primaryAutoLiabilityLimit`}
                name={`${BASE}.primaryAutoLiabilityLimit`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              <Grid item xs={4} />
              <Field
                xs={4}
                type="numeric"
                mode="dark"
                label="Year"
                id={`${BASE}.primaryAutomobileYear`}
                name={`${BASE}.primaryAutomobileYear`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              <Field
                xs={4}
                type="string"
                mode="dark"
                label="Make"
                id={`${BASE}.primaryAutomobileMake`}
                name={`${BASE}.primaryAutomobileMake`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              <Field
                xs={4}
                type="string"
                mode="dark"
                label="Model"
                id={`${BASE}.primaryAutomobileModel`}
                name={`${BASE}.primaryAutomobileModel`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              {/* 3.4 Additional automobiles */}
              <Field
                xs={6}
                type="select"
                mode="dark"
                label="Additional auto liability limit"
                options={additionalAutoLimitOptions}
                id={`${BASE}.additionalAutoLiabilityLimit`}
                name={`${BASE}.additionalAutoLiabilityLimit`}
                disabled={disabled}
                onChange={onChangeActive}
              />
            </Grid>

            {/* #35358 — the additional automobiles used to be a bare count, leaving the agent nowhere
                to name them while the primary auto got year/make/model. They are a list now; the
                rater's umb_num_addtl_auto is its length. */}
            <UmbrellaList
              title="Additional automobiles"
              fieldName={`${BASE}.additionalAutomobiles`}
              newItem={{ year: null, make: '', model: '' }}
              addLabel="Add an additional automobile"
              disabled={disabled}
              onChangeActive={onChangeActive}
              renderRow={vehicleRow('additionalAutomobiles')}
            />

            {/* 3.6 Youthful drivers */}
            <UmbrellaList
              title="Youthful drivers (under 26)"
              fieldName={`${BASE}.youthfulDrivers`}
              newItem={{ name: '', dob: null, licenseNumber: '', licenseState: '' }}
              addLabel="Add a youthful driver"
              disabled={disabled}
              onChangeActive={onChangeActive}
              renderRow={(index) => (
                <>
                  <Field
                    xs={4}
                    type="string"
                    mode="dark"
                    label="Name"
                    id={`${BASE}.youthfulDrivers[${index}].name`}
                    name={`${BASE}.youthfulDrivers[${index}].name`}
                    disabled={disabled}
                  />
                  <Field
                    xs={3}
                    type="date"
                    mode="dark"
                    label="Date of birth"
                    id={`${BASE}.youthfulDrivers[${index}].dob`}
                    name={`${BASE}.youthfulDrivers[${index}].dob`}
                    disabled={disabled}
                  />
                  <Field
                    xs={3}
                    type="string"
                    mode="dark"
                    label="License #"
                    id={`${BASE}.youthfulDrivers[${index}].licenseNumber`}
                    name={`${BASE}.youthfulDrivers[${index}].licenseNumber`}
                    disabled={disabled}
                  />
                  <Field
                    xs={2}
                    type="state"
                    mode="dark"
                    label="State"
                    id={`${BASE}.youthfulDrivers[${index}].licenseState`}
                    name={`${BASE}.youthfulDrivers[${index}].licenseState`}
                    disabled={disabled}
                  />
                </>
              )}
            />

            {/* 3.8 Antique / classic vehicles */}
            <UmbrellaList
              title="Antique / classic vehicles (road-registered)"
              fieldName={`${BASE}.antiqueClassicVehicles`}
              newItem={{ year: null, make: '', model: '' }}
              addLabel="Add an antique/classic vehicle"
              disabled={disabled}
              onChangeActive={onChangeActive}
              renderRow={vehicleRow('antiqueClassicVehicles')}
            />

            {/* 3.9 In-home day care */}
            <Label type="formSubTitle">Premises exposures</Label>
            <Grid container spacing={2}>
              <Field
                xs={4}
                type="numeric"
                mode="dark"
                label="Individuals in day care (min $500K CSL)"
                id={`${BASE}.numIndividualsCaredFor`}
                name={`${BASE}.numIndividualsCaredFor`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              <Field
                xs={4}
                type="string"
                mode="dark"
                label="Day care description"
                id={`${BASE}.dayCareDescription`}
                name={`${BASE}.dayCareDescription`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              <Field
                xs={4}
                type="string"
                mode="dark"
                label="Day care location"
                id={`${BASE}.dayCareLocation`}
                name={`${BASE}.dayCareLocation`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              {/* 3.10 / 3.11 pool & trampoline */}
              <Field
                xs={6}
                type="checkbox"
                mode="dark"
                label="Swimming pool on premises (min $500K CSL)"
                id={`${BASE}.hasSwimmingPool`}
                name={`${BASE}.hasSwimmingPool`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              <Field
                xs={6}
                type="checkbox"
                mode="dark"
                label="Trampoline on premises (min $500K CSL)"
                id={`${BASE}.hasTrampoline`}
                name={`${BASE}.hasTrampoline`}
                disabled={disabled}
                onChange={onChangeActive}
              />
            </Grid>

            {/* 3.12 additional owner-occupied residences */}
            <UmbrellaList
              title="Additional owner-occupied residences"
              fieldName={`${BASE}.additionalOwnerOccupiedResidences`}
              newItem={{ addressDetails: {}, combinedSingleLimit: '' }}
              addLabel="Add an owner-occupied residence"
              disabled={disabled}
              onChangeActive={onChangeActive}
              renderRow={residenceRow('additionalOwnerOccupiedResidences')}
            />

            {/* 3.13 rented / leased dwellings */}
            <UmbrellaList
              title="Residences rented/leased or held for rental"
              fieldName={`${BASE}.rentalDwellings`}
              newItem={{ addressDetails: {}, combinedSingleLimit: '' }}
              addLabel="Add a rental dwelling"
              disabled={disabled}
              onChangeActive={onChangeActive}
              renderRow={residenceRow('rentalDwellings')}
            />

            {/* 3.14 business pursuits */}
            <Grid container spacing={2}>
              <Field
                xs={3}
                type="checkbox"
                mode="dark"
                label="Business pursuits"
                id={`${BASE}.hasBusinessPursuits`}
                name={`${BASE}.hasBusinessPursuits`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              <Field
                xs={3}
                type="string"
                mode="dark"
                label="Description"
                id={`${BASE}.businessPursuitsDescription`}
                name={`${BASE}.businessPursuitsDescription`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              <Field
                xs={3}
                type="string"
                mode="dark"
                label="Location"
                id={`${BASE}.businessPursuitsLocation`}
                name={`${BASE}.businessPursuitsLocation`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              <Field
                xs={3}
                type="select"
                mode="dark"
                label="Limit"
                options={cslTierOptions}
                id={`${BASE}.businessPursuitsLimit`}
                name={`${BASE}.businessPursuitsLimit`}
                disabled={disabled}
                onChange={onChangeActive}
              />
            </Grid>

            {/* 3.15–3.18 watercraft */}
            <UmbrellaList
              title="Watercraft"
              fieldName={`${BASE}.watercraft`}
              newItem={{ category: '', description: '', horsepowerBand: '', lengthBand: '', speedBand: '' }}
              addLabel="Add a watercraft"
              disabled={disabled}
              onChangeActive={onChangeActive}
              renderRow={(index) => (
                <>
                  <Field
                    xs={3}
                    type="select"
                    mode="dark"
                    label="Type"
                    options={watercraftCategoryOptions}
                    id={`${BASE}.watercraft[${index}].category`}
                    name={`${BASE}.watercraft[${index}].category`}
                    disabled={disabled}
                  />
                  <Field
                    xs={3}
                    type="string"
                    mode="dark"
                    label="Description"
                    id={`${BASE}.watercraft[${index}].description`}
                    name={`${BASE}.watercraft[${index}].description`}
                    disabled={disabled}
                  />
                  <Field
                    xs={2}
                    type="select"
                    mode="dark"
                    label="Horsepower"
                    options={horsepowerBandOptions}
                    id={`${BASE}.watercraft[${index}].horsepowerBand`}
                    name={`${BASE}.watercraft[${index}].horsepowerBand`}
                    disabled={disabled}
                  />
                  <Field
                    xs={2}
                    type="select"
                    mode="dark"
                    label="Length"
                    options={lengthBandOptions}
                    id={`${BASE}.watercraft[${index}].lengthBand`}
                    name={`${BASE}.watercraft[${index}].lengthBand`}
                    disabled={disabled}
                  />
                  <Field
                    xs={2}
                    type="select"
                    mode="dark"
                    label="Speed"
                    options={speedBandOptions}
                    id={`${BASE}.watercraft[${index}].speedBand`}
                    name={`${BASE}.watercraft[${index}].speedBand`}
                    disabled={disabled}
                  />
                </>
              )}
            />

            {/* 3.19 / 3.20 recreational vehicles (ATV / snowmobile) */}
            <UmbrellaList
              title="ATVs & snowmobiles"
              fieldName={`${BASE}.recreationalVehicles`}
              newItem={{ category: '', description: '', horsepower: null }}
              addLabel="Add an ATV or snowmobile"
              disabled={disabled}
              onChangeActive={onChangeActive}
              renderRow={(index) => (
                <>
                  <Field
                    xs={4}
                    type="select"
                    mode="dark"
                    label="Type"
                    options={recVehicleCategoryOptions}
                    id={`${BASE}.recreationalVehicles[${index}].category`}
                    name={`${BASE}.recreationalVehicles[${index}].category`}
                    disabled={disabled}
                  />
                  <Field
                    xs={5}
                    type="string"
                    mode="dark"
                    label="Description"
                    id={`${BASE}.recreationalVehicles[${index}].description`}
                    name={`${BASE}.recreationalVehicles[${index}].description`}
                    disabled={disabled}
                  />
                  <Field
                    xs={3}
                    type="numeric"
                    mode="dark"
                    label="Horsepower"
                    id={`${BASE}.recreationalVehicles[${index}].horsepower`}
                    name={`${BASE}.recreationalVehicles[${index}].horsepower`}
                    disabled={disabled}
                  />
                </>
              )}
            />

            {/* 3.2 / 3.5 / 3.7 / 3.21 exclusions */}
            <Label type="formSubTitle">Exclusions</Label>
            <Grid container spacing={2}>
              <Field
                xs={6}
                type="checkbox"
                mode="dark"
                label="Exclude a named premises"
                id={`${BASE}.excludeNamedPremises`}
                name={`${BASE}.excludeNamedPremises`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              {umbrella.excludeNamedPremises && addressRow(`${BASE}.excludedPremises`, disabled)}
              <Field
                xs={6}
                type="checkbox"
                mode="dark"
                label="Exclude a named vehicle/watercraft"
                id={`${BASE}.excludeNamedAutomobile`}
                name={`${BASE}.excludeNamedAutomobile`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              {umbrella.excludeNamedAutomobile && (
                <Field
                  xs={6}
                  type="string"
                  mode="dark"
                  label="Excluded vehicle/watercraft description"
                  id={`${BASE}.excludedAutomobileDescription`}
                  name={`${BASE}.excludedAutomobileDescription`}
                  disabled={disabled}
                  onChange={onChangeActive}
                />
              )}
              <Field
                xs={6}
                type="checkbox"
                mode="dark"
                label="Exclude a named driver"
                id={`${BASE}.excludeNamedDriver`}
                name={`${BASE}.excludeNamedDriver`}
                disabled={disabled}
                onChange={onChangeActive}
              />
              {umbrella.excludeNamedDriver && (
                <>
                  <Field
                    xs={3}
                    type="string"
                    mode="dark"
                    label="Excluded driver name"
                    id={`${BASE}.excludedDriverName`}
                    name={`${BASE}.excludedDriverName`}
                    disabled={disabled}
                    onChange={onChangeActive}
                  />
                  <Field
                    xs={3}
                    type="date"
                    mode="dark"
                    label="Excluded driver DOB"
                    id={`${BASE}.excludedDriverDob`}
                    name={`${BASE}.excludedDriverDob`}
                    disabled={disabled}
                    onChange={onChangeActive}
                  />
                </>
              )}
              <Field
                xs={6}
                type="checkbox"
                mode="dark"
                label="Canine exclusion"
                id={`${BASE}.excludeCanine`}
                name={`${BASE}.excludeCanine`}
                disabled={disabled}
                onChange={onChangeActive}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Section>
  );
};

PersonalUmbrellaLiability.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onChangeActive: PropTypes.func.isRequired
};

// The youthful-driver and excluded-driver rows render `type="date"` fields, which need a
// MuiPickersUtilsProvider up the tree; the offer page provides none, so wrap here like FO4 and
// FarmSettings do (#35219 — adding a youthful driver crashed with "Can not find utils in context").
export default withDatePicker(PersonalUmbrellaLiability);
